import { useState, useCallback, useEffect, useMemo } from "react";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AsyncButton } from "./ui/async-button.js";
import { DropdownSelect } from "./DropdownButton.js";
import { FileInput } from "./ui/file-input.js";

interface AssetLite {
  id: string; assetTag: string; name: string; category: string; status: string;
  currency: string; purchasePrice: string | null; officeId: string | null;
}
interface Evidence {
  id: string; kind: string; fileName: string; s3Key: string; uploadedAt: string;
}
interface DisposalRequest {
  id: string; assetId: string; fromStatus: string; toStatus: string; reason: string;
  estimatedValue: string; currency: string; requiresApproval: boolean; autoApproved: boolean;
  status: string; requestedById: string; requestedAt: string; submittedAt: string | null;
  decidedById: string | null; decidedAt: string | null; decisionNotes: string | null;
  wipeRequired: boolean; wipeConfirmed: boolean;
  asset?: { id: string; assetTag: string; name: string; category: string; officeId: string | null };
  evidence?: Evidence[];
  requestedBy?: { id: string; firstName: string; lastName: string };
}

// Mirrors VALID_TRANSITIONS in assets.route.ts — only these three statuses can reach
// DISPOSED/RECYCLED, so the picker (and the server, independently) both enforce it.
const DISPOSABLE_FROM = ["RETIRED", "LOST", "STOLEN"];
const EVIDENCE_KINDS = [
  ["CERTIFICATE_OF_DESTRUCTION", "Certificate of Destruction"],
  ["LOSS_DECLARATION", "Loss Declaration"],
  ["WIPE_REPORT", "Wipe Report"],
  ["DISPOSAL_PHOTO", "Disposal Photo"],
  ["OTHER", "Other"],
] as const;

const EVIDENCE_KIND_OPTIONS = EVIDENCE_KINDS.map(([value, label]) => ({ value, label }));
const DISPOSE_AS_OPTIONS = [
  { value: "DISPOSED", label: "Disposed" },
  { value: "RECYCLED", label: "Recycled" },
];
const CURRENCY_OPTIONS = [
  { value: "INR", label: "INR" },
  { value: "USD", label: "USD" },
];

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px",
  background: "var(--color-surface-0)", width: "100%", boxSizing: "border-box",
};
const btnStyle = (color = "var(--color-brand-600)", text = "#fff"): React.CSSProperties => ({
  padding: "6px 14px", background: color, color: text,
  border: "none", borderRadius: "var(--radius-md)", cursor: "pointer",
  fontWeight: 600, fontSize: "13px",
});
const chip = (bg: string, fg: string): React.CSSProperties => ({
  display: "inline-block", fontSize: 11, padding: "3px 10px", borderRadius: 999, fontWeight: 700, background: bg, color: fg,
});
function statusChip(status: string) {
  const map: Record<string, [string, string]> = {
    DRAFT: ["var(--color-surface-200)", "var(--color-text-700)"],
    PENDING_FINANCE: ["var(--color-warning-bg)", "var(--color-warning-fg)"],
    APPROVED: ["var(--color-success-bg)", "var(--color-success-fg)"],
    REJECTED: ["var(--color-error-bg)", "var(--color-error-fg)"],
    CANCELLED: ["var(--color-surface-200)", "var(--color-text-500)"],
  };
  const [bg, fg] = map[status] ?? map["DRAFT"]!;
  return <span style={chip(bg, fg)}>{status.replace("_", " ")}</span>;
}

export function AssetDisposalPanel({ eligibleAssets, prefillAssetId, onPrefillConsumed, isFinance, isRegionalScoped, myOfficeId }: {
  eligibleAssets: AssetLite[]; prefillAssetId: string | null; onPrefillConsumed: () => void;
  isFinance: boolean; isRegionalScoped: boolean; myOfficeId: string;
}) {
  const [requests, setRequests] = useState<DisposalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ assetId: "", toStatus: "DISPOSED", reason: "", estimatedValue: "", currency: "INR" });
  const [creating, setCreating] = useState(false);

  const [selected, setSelected] = useState<DisposalRequest | null>(null);
  const [evidenceKind, setEvidenceKind] = useState<string>("CERTIFICATE_OF_DESTRUCTION");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<DisposalRequest[]>("/asset-disposals");
      setRequests(data);
    } catch (e) { logError("AssetDisposalPanel.loadRequests", e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  useEffect(() => {
    if (!prefillAssetId) return;
    const asset = eligibleAssets.find(a => a.id === prefillAssetId);
    if (asset) {
      setCreateForm({ assetId: asset.id, toStatus: "DISPOSED", reason: "", estimatedValue: asset.purchasePrice ?? "", currency: asset.currency });
      setShowCreate(true);
    }
    onPrefillConsumed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillAssetId]);

  async function openDetail(id: string) {
    setErr(""); setMsg("");
    try {
      const { data } = await api.get<DisposalRequest>(`/asset-disposals/${id}`);
      setSelected(data);
      setShowCreate(false);
    } catch (e) { setErr(errMsg(e) ?? "Failed to load request"); }
  }

  async function createRequest() {
    if (!createForm.assetId || !createForm.reason.trim() || !createForm.estimatedValue) {
      setErr("Asset, reason, and estimated value are required"); throw new Error("validation");
    }
    setCreating(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post<DisposalRequest>("/asset-disposals", {
        assetId: createForm.assetId, toStatus: createForm.toStatus, reason: createForm.reason.trim(),
        estimatedValue: Number(createForm.estimatedValue), currency: createForm.currency,
      });
      setShowCreate(false);
      setCreateForm({ assetId: "", toStatus: "DISPOSED", reason: "", estimatedValue: "", currency: "INR" });
      await loadRequests();
      await openDetail(data.id);
    } catch (e) { setErr(errMsg(e) ?? "Failed to create disposal request"); throw e; } finally { setCreating(false); }
  }

  async function uploadEvidence() {
    if (!selected || !evidenceFile) throw new Error("validation");
    setUploadingEvidence(true); setErr("");
    try {
      const r = await api.post<{ evidence: Evidence; uploadUrl: string }>(`/asset-disposals/${selected.id}/evidence`, {
        kind: evidenceKind, filename: evidenceFile.name, mimeType: evidenceFile.type || "application/octet-stream",
      });
      const uploadRes = await fetch(r.data.uploadUrl, { method: "PUT", body: evidenceFile, headers: { "Content-Type": evidenceFile.type || "application/octet-stream" } });
      if (!uploadRes.ok) throw new Error("S3 upload failed");
      await api.patch(`/asset-disposals/${selected.id}/evidence/${r.data.evidence.id}/confirm-upload`);
      setEvidenceFile(null);
      await openDetail(selected.id);
    } catch (e) { setErr(errMsg(e) ?? "Failed to upload evidence"); throw e; } finally { setUploadingEvidence(false); }
  }

  async function confirmWipe() {
    if (!selected) return;
    setErr("");
    try {
      await api.post(`/asset-disposals/${selected.id}/wipe-confirm`, {});
      await openDetail(selected.id);
    } catch (e) { setErr(errMsg(e) ?? "Failed to confirm wipe"); }
  }

  async function submitRequest() {
    if (!selected) throw new Error("validation");
    setSubmitting(true); setErr(""); setMsg("");
    try {
      await api.post(`/asset-disposals/${selected.id}/submit`, {});
      setMsg("Disposal request submitted");
      await loadRequests();
      await openDetail(selected.id);
    } catch (e) { setErr(errMsg(e) ?? "Failed to submit"); throw e; } finally { setSubmitting(false); }
  }

  async function decide(action: "approve" | "reject") {
    if (!selected) throw new Error("validation");
    if (action === "reject" && !rejectRemarks.trim()) { setErr("Remarks are required to reject"); throw new Error("validation"); }
    setSubmitting(true); setErr(""); setMsg("");
    try {
      await api.post(`/asset-disposals/${selected.id}/${action}`, action === "reject" ? { remarks: rejectRemarks.trim() } : {});
      setMsg(action === "approve" ? "Disposal approved" : "Disposal rejected");
      setRejectRemarks("");
      await loadRequests();
      await openDetail(selected.id);
    } catch (e) { setErr(errMsg(e) ?? "Failed to decide"); throw e; } finally { setSubmitting(false); }
  }

  async function cancelRequest() {
    if (!selected) return;
    if (!confirm("Cancel this disposal request?")) return;
    setErr("");
    try {
      await api.post(`/asset-disposals/${selected.id}/cancel`, {});
      await loadRequests();
      await openDetail(selected.id);
    } catch (e) { setErr(errMsg(e) ?? "Failed to cancel"); }
  }

  const pickableAssets = eligibleAssets.filter(a => DISPOSABLE_FROM.includes(a.status) && (!isRegionalScoped || a.officeId === myOfficeId));
  const assetOptions = useMemo(
    () => pickableAssets.map(a => ({ value: a.id, label: `${a.assetTag} — ${a.name} (${a.status})` })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eligibleAssets, isRegionalScoped, myOfficeId]
  );

  return (
    <div>
      {err && <div style={{ color: "var(--color-error-fg)", marginBottom: 12 }}>{err}</div>}
      {msg && <div style={{ color: "var(--color-success-fg)", marginBottom: 12 }}>{msg}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Disposal Requests</h3>
        {!showCreate && (
          <button style={btnStyle()} onClick={() => { setSelected(null); setShowCreate(true); }}>+ Request Disposal</button>
        )}
      </div>

      {showCreate && (
        <div style={{ border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", padding: 16, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>New Disposal Request</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label>Asset
              <DropdownSelect
                value={createForm.assetId}
                onChange={v => {
                  const asset = pickableAssets.find(a => a.id === v);
                  setCreateForm(f => ({ ...f, assetId: v, estimatedValue: asset?.purchasePrice ?? f.estimatedValue, currency: asset?.currency ?? f.currency }));
                }}
                options={assetOptions}
                placeholder="Select an asset…"
                ariaLabel="Asset"
                className="w-full justify-between"
                open={openDropdown === "asset"}
                onToggle={() => setOpenDropdown(d => (d === "asset" ? null : "asset"))}
                onClose={() => setOpenDropdown(d => (d === "asset" ? null : d))}
              />
            </label>
            <label>Dispose as
              <DropdownSelect
                value={createForm.toStatus}
                onChange={v => setCreateForm(f => ({ ...f, toStatus: v }))}
                options={DISPOSE_AS_OPTIONS}
                placeholder="Disposed"
                ariaLabel="Dispose as"
                clearable={false}
                className="w-full justify-between"
                open={openDropdown === "toStatus"}
                onToggle={() => setOpenDropdown(d => (d === "toStatus" ? null : "toStatus"))}
                onClose={() => setOpenDropdown(d => (d === "toStatus" ? null : d))}
              />
            </label>
            <label>Estimated value
              <input style={inputStyle} type="number" min="0" step="0.01" value={createForm.estimatedValue}
                onChange={e => setCreateForm(f => ({ ...f, estimatedValue: e.target.value }))} />
            </label>
            <label>Currency
              <DropdownSelect
                value={createForm.currency}
                onChange={v => setCreateForm(f => ({ ...f, currency: v }))}
                options={CURRENCY_OPTIONS}
                placeholder="INR"
                ariaLabel="Currency"
                clearable={false}
                className="w-full justify-between"
                open={openDropdown === "currency"}
                onToggle={() => setOpenDropdown(d => (d === "currency" ? null : "currency"))}
                onClose={() => setOpenDropdown(d => (d === "currency" ? null : d))}
              />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>Reason
              <textarea style={{ ...inputStyle, minHeight: 60 }} value={createForm.reason}
                onChange={e => setCreateForm(f => ({ ...f, reason: e.target.value }))} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <AsyncButton disabled={creating} onClick={() => createRequest()}>Create Draft</AsyncButton>
            <button style={btnStyle("var(--color-surface-200)","var(--color-text-700)")} onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "1px solid var(--color-surface-200)" }}>
            <th style={{ padding: 8 }}>Asset</th><th style={{ padding: 8 }}>To</th><th style={{ padding: 8 }}>Value</th>
            <th style={{ padding: 8 }}>Status</th><th style={{ padding: 8 }}>Requested</th><th style={{ padding: 8 }} />
          </tr>
        </thead>
        <tbody>
          {requests.map(r => (
            <tr key={r.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
              <td style={{ padding: 8 }}>{r.asset ? `${r.asset.assetTag} — ${r.asset.name}` : r.assetId}</td>
              <td style={{ padding: 8 }}>{r.toStatus}</td>
              <td style={{ padding: 8 }}>{r.currency} {r.estimatedValue}</td>
              <td style={{ padding: 8 }}>{statusChip(r.status)}</td>
              <td style={{ padding: 8 }}>{new Date(r.requestedAt).toLocaleDateString()}</td>
              <td style={{ padding: 8 }}><button style={btnStyle("var(--color-info-bg)","var(--color-info-fg)")} onClick={() => openDetail(r.id)}>Open</button></td>
            </tr>
          ))}
          {!loading && requests.length === 0 && (
            <tr><td colSpan={6} style={{ padding: 16, textAlign: "center", color: "var(--color-text-500)" }}>No disposal requests yet</td></tr>
          )}
        </tbody>
      </table>

      {selected && (
        <div style={{ border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", padding: 16, marginTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 style={{ margin: 0 }}>{selected.asset?.assetTag} — {selected.asset?.name} {statusChip(selected.status)}</h4>
            <button style={btnStyle("var(--color-surface-200)","var(--color-text-700)")} onClick={() => setSelected(null)}>Close</button>
          </div>
          <p style={{ color: "var(--color-text-500)" }}>{selected.fromStatus} → {selected.toStatus} · {selected.currency} {selected.estimatedValue} · {selected.reason}</p>
          {selected.decisionNotes && <p><strong>Decision notes:</strong> {selected.decisionNotes}</p>}

          {selected.status === "DRAFT" && (
            <>
              <div style={{ marginTop: 12 }}>
                <strong>Evidence</strong>
                <ul>
                  {(selected.evidence ?? []).map(e => <li key={e.id}>{e.kind.replace(/_/g, " ")} — {e.fileName}</li>)}
                  {(selected.evidence ?? []).length === 0 && <li style={{ color: "var(--color-text-500)" }}>None attached yet</li>}
                </ul>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <DropdownSelect
                    value={evidenceKind}
                    onChange={setEvidenceKind}
                    options={EVIDENCE_KIND_OPTIONS}
                    placeholder="Evidence kind"
                    ariaLabel="Evidence kind"
                    clearable={false}
                    open={openDropdown === "evidence"}
                    onToggle={() => setOpenDropdown(d => (d === "evidence" ? null : "evidence"))}
                    onClose={() => setOpenDropdown(d => (d === "evidence" ? null : d))}
                  />
                  <FileInput size="sm" value={evidenceFile} onChange={setEvidenceFile} />
                  <AsyncButton disabled={!evidenceFile || uploadingEvidence} onClick={() => uploadEvidence()}>
                    Upload
                  </AsyncButton>
                </div>
              </div>

              {selected.wipeRequired && (
                <div style={{ marginTop: 12 }}>
                  <strong>Data wipe: </strong>
                  {selected.wipeConfirmed ? <span style={chip("var(--color-success-bg)","var(--color-success-fg)")}>Confirmed</span> : (
                    <button style={btnStyle("var(--color-warning-bg)","var(--color-warning-fg)")} onClick={confirmWipe}>Confirm data wipe</button>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <AsyncButton disabled={submitting} onClick={() => submitRequest()}>Submit</AsyncButton>
                <button style={btnStyle("var(--color-error-bg)","var(--color-error-fg)")} onClick={cancelRequest}>Cancel Request</button>
              </div>
            </>
          )}

          {selected.status === "PENDING_FINANCE" && isFinance && (
            <div style={{ marginTop: 16 }}>
              <textarea style={{ ...inputStyle, minHeight: 50 }} placeholder="Remarks (required to reject)"
                value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)} />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <AsyncButton variant="success" disabled={submitting} onClick={() => decide("approve")}>Approve</AsyncButton>
                <AsyncButton variant="destructive" disabled={submitting} onClick={() => decide("reject")}>Reject</AsyncButton>
              </div>
            </div>
          )}

          {selected.status === "PENDING_FINANCE" && !isFinance && (
            <div style={{ marginTop: 16 }}>
              <button style={btnStyle("var(--color-error-bg)","var(--color-error-fg)")} onClick={cancelRequest}>Cancel Request</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
